import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'redux-form'
import {
  CheckboxGroup,
  CheckboxWithText,
  AutoSaveStatusIndicator,
} from 'components'
import * as Types from 'types'
import {
  formatObjectsToIds,
  parseIdsToObjects,
  serializeOptions,
  yankBy,
  isOtherType,
  isNoneType,
  getNoneTypeId,
  orderLastBy,
} from 'utils'

const propTypes = {
  resourceTypes: PropTypes.arrayOf(Types.resourceType).isRequired,
  prefix: PropTypes.string,
  change: PropTypes.func.isRequired,
  requestedResources: PropTypes.array,
}

const defaultProps = {
  prefix: '',
  requestedResources: [],
}

function ArtsProgrammingResourceForm({
  resourceTypes,
  prefix = '',
  change,
  requestedResources,
}) {
  const base = prefix ? `${prefix}.` : ''
  const resourcesName = `${base}requestedResources`
  const otherName = `${base}otherText`

  // Pull "Other" out so it can be rendered as a checkbox with a text field.
  const [otherResourceType, definedResourceTypes] = useMemo(
    () => yankBy(resourceTypes, isOtherType),
    [resourceTypes]
  )
  const noneTypeId = useMemo(
    () => getNoneTypeId(resourceTypes),
    [resourceTypes]
  )

  // Flag the "None" option so CheckboxGroup enforces its mutual exclusivity
  // (selecting None clears the others; selecting another clears None). Keep it
  // ordered last in the list.
  const options = useMemo(() => {
    const ordered = orderLastBy(isNoneType, definedResourceTypes)
    return serializeOptions(ordered).map((option) =>
      option.value === noneTypeId ? { ...option, none: true } : option
    )
  }, [definedResourceTypes, noneTypeId])

  // Selecting "None" also clears the "Other" text (None is exclusive of all).
  const handleResourcesChange = useCallback(
    (_event, newValue) => {
      const selectedIds = (newValue || []).map((r) => r.resourceTypeId)
      if (selectedIds.includes(noneTypeId)) change(otherName, '')
    },
    [change, noneTypeId, otherName]
  )

  // Selecting "Other" deselects "None".
  const handleOtherChange = useCallback(
    (_event, newValue) => {
      if (!newValue) return
      const hasNone = (requestedResources || []).some(
        (r) => r.resourceTypeId === noneTypeId
      )
      if (!hasNone) return
      change(
        resourcesName,
        requestedResources.filter((r) => r.resourceTypeId !== noneTypeId)
      )
    },
    [change, noneTypeId, requestedResources, resourcesName]
  )

  return (
    <div>
      <Field
        name={resourcesName}
        label={false}
        component={CheckboxGroup}
        format={formatObjectsToIds('resourceTypeId')}
        parse={parseIdsToObjects('resourceTypeId')}
        options={options}
        onChange={handleResourcesChange}
      />
      {otherResourceType && (
        <Field
          name={otherName}
          label={otherResourceType.displayName}
          placeholder="Description..."
          component={CheckboxWithText}
          onChange={handleOtherChange}
        />
      )}
      <AutoSaveStatusIndicator {...{ saveSucceeded: false }} />
    </div>
  )
}

ArtsProgrammingResourceForm.propTypes = propTypes
ArtsProgrammingResourceForm.defaultProps = defaultProps

export default ArtsProgrammingResourceForm
